import React from 'react';
import { DashboardWidget } from './DashboardWidget';
import { TableWidget, TableRow } from './TableWidget';
import { useAuth } from '../../contexts/AuthContext';
import { fetchUserAccount } from '../../api/userAccount';
import { fetchTokenPrice } from '../../api/userPositions';
import { TOKENS } from '@sepori/shared/registry';

interface TokenBalancesWidgetProps {
  className?: string;
}

// Removed unused interface

export const TokenBalancesWidget: React.FC<TokenBalancesWidgetProps> = ({ className }) => {
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<TableRow[]>([]);
  const [refreshing, setRefreshing] = React.useState(false);
  const [lastUpdated, setLastUpdated] = React.useState<Date | null>(null);

  const loadData = async (bypassCache = false) => {
    try {
      setLoading(true);
      setError(null);

      // Get user account data for bank balance
      const accountResponse = await fetchUserAccount(user!.id);
      const account = accountResponse.account;

      // Get all token balances and prices
      const tokenEntries = Object.values(TOKENS).sort((a, b) => a.display_order - b.display_order);
      const balancePromises = tokenEntries.map(async (token) => {
        try {
          let balance = '0';

          if (token.symbol === 'ETH') {
            // Native ETH balance
            const response = await fetch(`/api/wallet/${user!.walletAddress}/eth-balance`);
            if (response.ok) {
              const data = await response.json();
              balance = data.balance;
            }
          } else {
            // ERC20 token balance
            const response = await fetch(`/api/token/${token.address}/balance/${user!.walletAddress}`);
            if (response.ok) {
              const data = await response.json();
              balance = data.balance;
            }
          }

          // Get token price
          const priceResponse = await fetchTokenPrice(token.address, { bypassCache });
          const price = priceResponse.priceUsd;

          // Convert balance from wei to readable format
          const balanceNumber = Number(balance) / Math.pow(10, token.decimals);

          return {
            symbol: token.symbol,
            address: token.address,
            balance: balanceNumber,
            decimals: token.decimals,
            display_order: token.display_order,
            price
          };
        } catch (err) {
          console.warn(`Failed to fetch balance for ${token.symbol}:`, err);
          return null;
        }
      });

      const tokenBalances = (await Promise.all(balancePromises))
        .filter((balance): balance is NonNullTokenBalance => balance !== null && balance.balance > 0);

      // Create table rows
      const newRows: TableRow[] = [];

      let totalValue = 0;

      // Add bank balance row if > 0
      const bankBalance = Number(account.bank_balance_usd);
      if (bankBalance > 0) {
        totalValue += bankBalance;
        newRows.push({
          token: 'USD',
          amount: bankBalance.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }),
          value: `$${bankBalance.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`
        });
      }

      // Add wallet token balances
      tokenBalances.forEach(balance => {
        if (balance && balance.balance > 0) {
          const value = balance.balance * balance.price;
          totalValue += value;
          newRows.push({
            token: balance.symbol,
            amount: balance.balance.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4
            }),
            value: `$${value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`
          });
        }
      });

      // Append veAERO row at the end if amount > 0
      const veAeroAmount = Number((account as any).veaero_amount ?? 0);
      if (veAeroAmount > 0) {
        try {
          const aero = TOKENS['AERO'];
          const priceResponse = await fetchTokenPrice(aero.address, { bypassCache });
          const price = priceResponse.priceUsd;
          const value = veAeroAmount * price;
          totalValue += value;
          newRows.push({
            token: 'veAERO',
            amount: veAeroAmount.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4
            }),
            value: `$${value.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}`
          });
        } catch (err) {
          console.warn('Failed to append veAERO value:', err);
        }
      }

      // Add total value row
      newRows.push({
        token: '',
        amount: '',
        value: `$${totalValue.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`,
        valueClass: 'total-row-highlight'
      });

      setRows(newRows);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Failed to load token balances:', err);
      setError(err instanceof Error ? err.message : 'Failed to load token balances');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      await loadData(true); // bypassCache=true for manual refresh
    } catch (err) {
      console.error('Failed to refresh token balances:', err);
    } finally {
      setRefreshing(false);
    }
  };

  React.useEffect(() => {
    if (!user?.id || !user?.walletAddress) return;
    loadData();
  }, [user?.id, user?.walletAddress]);

  if (loading) {
    return (
      <div className={`dashboard-widget token-values ${className}`}>
        <div className="widget-header">
          <h3 className="widget-title">Token Values</h3>
        </div>
        <div className="widget-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading your balances...</p>
          </div>
        </div>
        <div className="widget-refresh-bottom">
          <button
            className="refresh-button"
            disabled={true}
            style={{ pointerEvents: 'none' }}
          >
            Refresh
          </button>
          {lastUpdated && (
            <span className="last-updated">
              Last updated:<br/>{lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <DashboardWidget
      title="Token Values"
      loading={false}
      error={error || undefined}
      className={`token-values ${className}`}
      onRefresh={loading ? undefined : refreshData}
      refreshing={refreshing || loading}
      lastUpdated={lastUpdated ?? undefined}
      refreshButtonBottom={true}
    >
      <TableWidget
        rows={rows}
        showLabelColumn={false}
        columnHeaders={{
          token: 'Token',
          amount: 'Amount',
          value: 'Value'
        }}
      />
    </DashboardWidget>
  );
};

interface NonNullTokenBalance {
  symbol: "AERO" | "WETH" | "USDC" | "CBBTC" | "MSUSD" | "KVCM" | "ETH";
  address: `0x${string}`;
  balance: number;
  decimals: number;
  display_order: number;
  price: number;
}
